import React from "react";
import dayjs from "dayjs";
import { faxAiView, faxSource } from "../fax/faxAi";
import { faxIsDeleted, faxJunkHint, faxProviderName, faxUserName, faxWorkLine, faxWorkOf } from "../fax/faxWork";
import { faxRowAiPending } from "../fax/faxWorkParts";

/*
 * One fax in Inbox > Faxes (fax/faxtable.jsx with `inboxStyle`; owner 10-20: "list not
 * readable"). Communication > Fax and OSA keep their own row.
 *   sender 15 px semibold (one line) · phone 13 px · the AI label as a full-text pill
 *   (wraps) · summary 14 px #374151, up to 3 lines · "For <provider>" / assignee /
 *   Done / Review / Deleted as small pills · the latest work note · received time.
 *   The checkbox (bulk assign) sits centred on the sender line, the ••• menu top right.
 */
export default function InboxFaxListItem({
  row,
  index,
  active,
  ownNames,
  providers = [],
  staff = [],
  workAvailable,
  bulkOn,
  picked,
  onPick,
  onSelect,
  menuOpen,
  onMenu,
  menuRef,
  onView,
  onDelete,
  onDeleteAsSpam,
}) {
  const src = faxSource(row, ownNames);
  const view = faxAiView(row);
  const w = faxWorkOf(row);
  const deleted = faxIsDeleted(row);
  const prov = workAvailable && w.providerid !== null ? faxProviderName(providers, w.providerid) : "";
  const who = workAvailable && w.assignee_user_id !== null ? faxUserName(staff, w.assignee_user_id, w.assignee_name) : "";
  const note = workAvailable ? faxWorkLine(row) : "";
  // no provider yet and the AI has not read it (owner 10-22): "AI pending", not "No provider"
  const aiPending = workAvailable && !prov && faxRowAiPending(row);
  const junk = !deleted ? faxJunkHint(row) : null;
  const tone = (on, off) => (active ? on : off);
  const pill = "inline-flex items-center rounded-full px-2 py-[2px] text-[12px] font-medium leading-4";
  return (
    <li
      className={`relative cursor-pointer rounded-[10px] border transition-all ${
        active ? "border-[#50869A] bg-[#50869A] text-white shadow-md" : "border-[#E5E7EB] bg-white hover:bg-[#F8FAFC]"
      }`}
      onClick={onSelect}
      data-testid="inbox-fax-row"
    >
      <div className="flex flex-col gap-1.5 p-3 pr-9 text-left">
        <div className="flex min-w-0 items-center gap-2">
          {bulkOn ? (
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0"
              aria-label={`Select fax ${index}`}
              checked={picked}
              onClick={(e) => e.stopPropagation()}
              onChange={onPick}
              data-testid="fax-pick"
            />
          ) : (
            <span className={`shrink-0 text-[13px] font-medium ${tone("text-white", "text-[#475467]")}`}>{index}</span>
          )}
          <span
            className={`min-w-0 truncate text-[15px] font-semibold leading-5 ${tone("text-white", "text-[#101828]")}`}
            title={src.heading}
            data-testid="fax-row-heading"
          >
            {src.heading}
          </span>
        </div>
        <div className={`text-[13px] leading-4 ${tone("text-white/85", "text-[#475467]")}`}>
          <span data-testid="fax-row-sub">{src.sub}</span>
          {src.callerHint && <span> · {src.callerHint}</span>}
        </div>
        {view && view.pending && <div className={`text-[13px] italic ${tone("text-white/80", "text-[#667085]")}`}>Reading the fax…</div>}
        {view && !view.pending && view.label && (
          <div>
            <span className={`${pill} whitespace-normal break-words ${tone("bg-white/20 text-white", "bg-[#EEF4FF] text-[#3538CD]")}`} data-testid="fax-ai-label">
              {view.label}
            </span>
          </div>
        )}
        {view && !view.pending && view.summary && (
          <div
            className={`line-clamp-3 break-words text-[14px] leading-5 ${tone("text-white", "text-[#374151]")}`}
            title={view.summary}
            data-testid="fax-ai-summary"
          >
            {view.summary}
          </div>
        )}
        {(prov || who || aiPending || deleted || w.status === "done" || w.needs_review) && (
          <div className="flex flex-wrap items-center gap-1">
            {deleted && <span className={`${pill} ${tone("bg-white/20 text-white", "bg-[#FEF3F2] text-[#B42318]")}`}>Deleted</span>}
            {!deleted && w.status === "done" && <span className={`${pill} ${tone("bg-white/20 text-white", "bg-[#ECFDF3] text-[#027A48]")}`}>Done</span>}
            {w.needs_review && <span className={`${pill} ${tone("bg-white/20 text-white", "bg-[#FFFAEB] text-[#B54708]")}`}>Review</span>}
            {prov && (
              <span className={`${pill} ${tone("bg-white/20 text-white", "bg-[#F2F4F7] text-[#344054]")}`} data-testid="fax-work-provider">
                For {prov}
              </span>
            )}
            {aiPending && (
              <span
                className={`${pill} italic ${tone("bg-white/20 text-white", "bg-[#F2F4F7] text-[#667085]")}`}
                title="The AI has not read this fax yet: its provider may still come from the fax"
                data-testid="fax-work-ai-pending"
              >
                AI pending
              </span>
            )}
            {who && (
              <span className={`${pill} ${tone("bg-white/20 text-white", "bg-[#1D2951]/[0.07] text-[#1D2951]")}`} data-testid="fax-work-assignee">
                → {who}
              </span>
            )}
          </div>
        )}
        {note && <div className={`line-clamp-2 text-[13px] ${tone("text-white/85", "text-[#475467]")}`}>{note}</div>}
        {junk && (
          <div className="flex flex-wrap items-center gap-2 text-[13px]">
            <span className={`${pill} ${tone("bg-white/20 text-white", "bg-[#FFFAEB] text-[#B54708]")}`}>{junk.text}</span>
            <button
              type="button"
              className={`underline ${tone("text-white", "text-[#B42318]")}`}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteAsSpam();
              }}
            >
              Delete as spam
            </button>
          </div>
        )}
        {row.creationTime && (
          <div className={`text-[12px] ${tone("text-white/80", "text-[#667085]")}`}>{dayjs(row.creationTime).format("MM-DD-YYYY h:mm A")}</div>
        )}
      </div>
      <div className="absolute right-1.5 top-2">
        <button
          type="button"
          aria-label="Fax actions"
          className={`flex h-8 w-8 items-center justify-center rounded-md text-[18px] leading-none ${tone("text-white hover:bg-white/10", "text-[#667085] hover:bg-[#F2F4F7]")}`}
          onClick={(e) => {
            e.stopPropagation();
            onMenu();
          }}
          data-testid="inbox-fax-menu"
        >
          •••
        </button>
        {menuOpen && (
          <div
            ref={menuRef}
            className="absolute right-0 top-9 z-50 w-[120px] rounded-md border bg-white text-left text-[14px] shadow-md"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="block w-full px-3 py-2 text-left text-[#232323] hover:bg-gray-100" onClick={onView}>
              View
            </button>
            {!deleted && (
              <button type="button" className="block w-full px-3 py-2 text-left text-red-600 hover:bg-gray-100" onClick={onDelete}>
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
